/**
 * Page and section headings.
 *
 * The prototype repeated the same inline Space Grotesk heading style at roughly
 * two dozen sites, sometimes with an icon and a subtitle. Consolidated here so
 * the type scale has one definition.
 */
import type { ReactNode } from 'react';
import { c } from '../../styles/theme';

export interface SectionHeadingProps {
  title: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Optional leading icon, typically a lucide icon element. */
  icon?: ReactNode;
  /** Right-aligned content on the title row, e.g. an action button. */
  action?: ReactNode;
  /** Heading size. `page` = 16px page titles, `sub` = 13px sub-sections. */
  size?: 'page' | 'sub';
  /** Renders the correct heading level for the document outline. */
  as?: 'h1' | 'h2' | 'h3';
  /** Bottom margin. Defaults differ between the two sizes, matching the source. */
  marginBottom?: number;
}

export default function SectionHeading({
  title,
  subtitle,
  icon,
  action,
  size = 'page',
  as: Tag = 'h2',
  marginBottom,
}: SectionHeadingProps) {
  const fontSize = size === 'page' ? 16 : 13;
  // The prototype's headings were <p> elements, so the page had no outline at
  // all. Rendering a real heading element restores document structure without
  // changing a pixel: the font size, weight, family and margins below are the
  // same values, and the browser's default heading margins are overridden.
  const gap = subtitle ? 4 : (marginBottom ?? 14);

  return (
    <div style={{ marginBottom: subtitle ? (marginBottom ?? 16) : undefined }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: action ? 'space-between' : undefined,
          gap: 8,
          marginBottom: gap,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon}
          <Tag
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize,
              margin: 0,
              color: c.text1,
            }}
          >
            {title}
          </Tag>
        </span>
        {action}
      </div>
      {subtitle && <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>{subtitle}</p>}
    </div>
  );
}
